/**
 * Helpers for the multi-clip selection model on the timeline.
 *
 * The reducer stores selection as `selectedClipIds: Set<string>` containing the
 * IDs of every selected clip (video AND audio — cross-type selection is
 * allowed). These helpers derive the previous "single-selection" projection
 * (the first selected clip's id and clip type, or 'mixed') used by panels
 * that aren't multi-aware yet.
 */

import type { TimelineAudioClip, TimelineClip, TimelineState } from './types'

export type SplitTarget =
  | { type: 'video'; clip: TimelineClip }
  | { type: 'audio'; clip: TimelineAudioClip }

export function splitRangeForClip(clip: TimelineClip | TimelineAudioClip, type: 'video' | 'audio') {
  const margin = type === 'video' ? 1 / (30 * clip.speed) : 1e-4
  const min = clip.startOffset + margin
  const max = clip.startOffset + clip.effectiveDuration - margin
  return max < min ? null : { min, max }
}

/** Prefer a selected clip under the cursor, otherwise the topmost video/audio. */
export function splitTargetAtPlayhead(state: TimelineState): SplitTarget | null {
  const targets: SplitTarget[] = [
    ...[...state.clips].sort((a, b) => a.trackIndex - b.trackIndex).map((clip): SplitTarget => ({ type: 'video', clip })),
    ...[...state.audioClips].sort((a, b) => a.trackIndex - b.trackIndex).map((clip): SplitTarget => ({ type: 'audio', clip })),
  ]
  const candidates = targets.filter(({ clip, type }) => {
    const range = splitRangeForClip(clip, type)
    return range && state.playheadPosition >= range.min && state.playheadPosition <= range.max
  })
  return candidates.find(({ clip }) => state.selectedClipIds.has(clip.id)) ?? candidates[0] ?? null
}

export type SelectedClipType = 'video' | 'audio' | 'mixed' | null

/**
 * Snapshot of the legacy "single selection" projection. Only safe to use when
 * exactly one clip is selected; for multi-selection, callers should branch on
 * `selectedClipIds.size`.
 */
export interface SelectionSummary {
  /** First selected clip id (insertion order), or null if no selection */
  primaryId: string | null
  /** Type of the primary selection — 'mixed' if cross-type, else 'video' / 'audio' / null */
  type: SelectedClipType
}

export function summarizeSelection(state: TimelineState): SelectionSummary {
  if (state.selectedClipIds.size === 0) {
    return { primaryId: null, type: null }
  }
  let firstId: string | null = null
  let hasVideo = false
  let hasAudio = false
  for (const id of state.selectedClipIds) {
    if (firstId === null) firstId = id
    if (state.clips.some((c) => c.id === id)) hasVideo = true
    if (state.audioClips.some((c) => c.id === id)) hasAudio = true
  }
  let type: SelectedClipType = null
  if (hasVideo && hasAudio) type = 'mixed'
  else if (hasVideo) type = 'video'
  else if (hasAudio) type = 'audio'
  return { primaryId: firstId, type }
}

/** True if `clipId` is part of the current selection. */
export function isSelected(state: TimelineState, clipId: string): boolean {
  return state.selectedClipIds.has(clipId)
}

/** Convenience: filter to just the selected video clips. */
export function selectedVideoClips(state: TimelineState): TimelineClip[] {
  return state.clips.filter((c) => state.selectedClipIds.has(c.id))
}

/** Convenience: filter to just the selected audio clips. */
export function selectedAudioClips(state: TimelineState): TimelineAudioClip[] {
  return state.audioClips.filter((c) => state.selectedClipIds.has(c.id))
}
